import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Copy, ExternalLink, Info } from "lucide-react";
import { Dash } from "@/components/Dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { DateRangeFilter, useDateRangeFilter } from "@/components/DateRangeFilter";
import { useFileViewer } from "@/components/FileViewerDialog";
import { FilterBar } from "@/components/FilterBar";
import { Notice } from "@/components/Notice";
import { url } from "@/lib/basePath";
import { Pagination } from "@/components/Pagination";
import { SortableTH } from "@/components/SortableTH";
import { useColumnSort } from "@/hooks/useColumnSort";
import { usePagedList } from "@/hooks/usePagedList";
import { useStatusCounts } from "@/hooks/useStatusCounts";
import { toastApiError } from "@/lib/toast";
import { cn } from "@/lib/utils";
import ShareLinkCard from "@/features/onboarding/components/ShareLinkCard";
import NextStepsCard from "@/features/onboarding/components/NextStepsCard";
import { PartnerRegenerateLinkButton } from "@/features/onboarding/components/RegenerateLinkButton";
import {
  partnerForms,
  PARTNER_FORM_STATUS_TABS,
  type PartnerFormItem,
  type PartnerLink,
} from "@/features/onboarding/api/onboarding.api";
import { users, type UserDetail } from "@/features/users/api/users.api";
import { UserSelect } from "@/features/users/components/UserSelect";
import { formatDateTime } from "@/utils/dates";

const NEW_PARTNER = "__new__";
const NEW_PARTNER_OPTIONS = [{ value: NEW_PARTNER, label: "New partner (creates a new user)" }];

const NEXT_STEPS: ReactNode[] = [
  "Share the partner link with your prospect.",
  "They complete and submit the partner form.",
  "The submission appears below with a status.",
  <>
    Review the submission and use <strong>Create New User</strong> when ready.
  </>,
];

type SortKey = "submittedAt" | "name" | "business" | "email" | "status";

const STATUS_GROUPS = Object.fromEntries(
  PARTNER_FORM_STATUS_TABS.map((t) => [t, [t]]),
);

const COLUMN_MAP: Record<SortKey, string> = {
  submittedAt: "submittedAt",
  name: "firstName",
  business: "legalName",
  email: "email",
  status: "status",
};

export default function PartnerFormPage() {
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [path, setPath] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!selected) {
      setSelectedUser(null);
      return;
    }
    let cancelled = false;
    users.get(selected)
      .then((u) => { if (!cancelled) setSelectedUser(u); })
      .catch(() => { if (!cancelled) setSelectedUser(null); });
    return () => { cancelled = true; };
  }, [selected]);

  useEffect(() => {
    let cancelled = false;
    partnerForms.link(selected || null)
      .then((r) => { if (!cancelled) { applyLink(r); } })
      .catch((e) => {
        if (cancelled) return;
        toastApiError(e, "Couldn't build the link.");
      });
    return () => { cancelled = true; };
  }, [selected]);

  function applyLink(r: PartnerLink) {
    setPath(r.path);
    setExpiresAt(r.expiresAt ?? null);
    setCopied(false);
  }

  const link = useMemo(() => (path ? url(path) : ""), [path]);
  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't reach the clipboard — select the link and copy it manually.");
    }
  }

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Partner Form</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Share a partner link with prospects and review submitted partner
          forms. Pick an existing user to update their profile, or leave it on
          "New partner" to collect a brand-new one.
        </p>
      </header>

      <div className="space-y-6">
        <Card>
          <CardHeader className="border-b py-3">
            <CardTitle className="text-base font-semibold text-slate-900">Partner Form Link Setup</CardTitle>
            <CardDescription>
              Create and manage the partner form link you share with prospects.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-5">
            <div className="grid gap-5 lg:grid-cols-2 lg:gap-6">
              <div className="space-y-3">
                <Field label="Send the form to" htmlFor="sendFormTo">
                  <UserSelect
                    id="sendFormTo"
                    value={selected || NEW_PARTNER}
                    onValueChange={(v) => setSelected(v === NEW_PARTNER ? "" : v)}
                    pinnedOptions={NEW_PARTNER_OPTIONS}
                  />
                </Field>

                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {selectedUser
                      ? <>Submissions update <span className="font-medium text-slate-700">{selectedUser.name}</span>'s profile automatically.</>
                      : <>Submissions appear below as a new partner; use "Create New User" to add them.</>}
                  </span>
                </p>
              </div>

              <Field label="Partner Form Link">
                <div className="space-y-2">
                  <Input
                    readOnly
                    value={link}
                    onFocus={(e) => e.currentTarget.select()}
                    className="bg-muted font-mono text-slate-700"
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    {selected && (
                      <PartnerRegenerateLinkButton
                        userId={selected}
                        userName={selectedUser?.name}
                        onDone={applyLink}
                      />
                    )}
                    <Button type="button" onClick={copy} disabled={!link} className="whitespace-nowrap">
                      <Copy />
                      {copied ? "Copied ✓" : "Copy link"}
                    </Button>
                    {link ? (
                      <Button asChild variant="outline" className="whitespace-nowrap">
                        <a href={link} target="_blank" rel="noreferrer">
                          <ExternalLink />
                          Open link
                        </a>
                      </Button>
                    ) : (
                      <Button type="button" variant="outline" disabled className="whitespace-nowrap">
                        <ExternalLink />
                        Open link
                      </Button>
                    )}
                  </div>
                  {link && expiresAt && (
                    <p className="text-xs text-muted-foreground">
                      Link stops working on{" "}
                      {formatDateTime(expiresAt, { year: "numeric", month: "short", day: "numeric" })}
                      , then a new one is issued automatically.
                    </p>
                  )}
                </div>
              </Field>
            </div>
          </CardContent>
        </Card>

        {link && (
          <ShareLinkCard
            link={link}
            formName="partner form"
            qrFilename="partner-form-link.png"
            defaultEmail={selectedUser?.email ?? ""}
            emailHint={
              selectedUser
                ? "We've filled in the user's email on file — change it to send somewhere else."
                : undefined
            }
            onSend={async (to, toName) => {
              const r = await partnerForms.sendLink({
                userId: selected || null,
                email: to || undefined,
                toName,
              });
              return r.to;
            }}
          />
        )}

        <NextStepsCard steps={NEXT_STEPS} />

        <SubmissionsTable />
      </div>
    </>
  );
}

function SubmissionsTable() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [created, setCreated] = useState<Record<string, string>>({});
  const confirm = useConfirm();
  const viewFile = useFileViewer();
  const { range, setRange, since, active: dateFiltered } = useDateRangeFilter();

  const {
    data, loading, loaded, error, forbidden,
    keyword, status, sortBy, sortDir,
    setPage, setKeyword, setStatus, setSort, reload,
  } = usePagedList<PartnerFormItem>(partnerForms.list, {
    pageSize: 10,
    initialSortBy: "submittedAt",
    initialSortDir: "desc",
    extra: { since },
  });

  const counts = useStatusCounts(partnerForms.summary, STATUS_GROUPS, { keyword, since });

  const { thProps } = useColumnSort<SortKey>(COLUMN_MAP, sortBy, sortDir, setSort);

  function viewDocument(id: string, which: "license" | "check") {
    viewFile({
      name: which === "license" ? "Driver's license" : "Voided check",
      load: () => partnerForms.documentFile(id, which),
      errorMessage: "Couldn't open the document — the file may no longer be available.",
    });
  }

  async function createUser(f: PartnerFormItem) {
    const who = f.contactName || f.legalName || f.email;
    if (!(await confirm({
      title: "Create new user?",
      description: `Create a portal user${who ? ` for ${who}` : ""}${f.email ? ` (${f.email})` : ""}? A temporary password will be generated and this submission will be marked as processed.`,
      confirmLabel: "Create user",
    }))) {
      return;
    }
    const id = f.id;
    setBusyId(id);
    try {
      const r = await partnerForms.createUser(id);
      setCreated((c) => ({ ...c, [id]: r.tempPassword }));
      reload();
    } catch (e) {
      toastApiError(e, "Couldn't create the user.");
    } finally {
      setBusyId(null);
    }
  }

  if (forbidden) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          You don't have permission to view partner form submissions.
        </CardContent>
      </Card>
    );
  }

  const filtered = keyword.trim() !== "" || status !== "" || dateFiltered;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b py-3 flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base font-semibold text-slate-900">Submitted Partner Forms</CardTitle>
          <CardDescription>Track and review all submitted partner forms.</CardDescription>
        </div>
        {loaded && !error && (
          <Badge variant="info" className="shrink-0 tabular-nums">
            {counts?.all ?? data.totalCount} total
          </Badge>
        )}
      </CardHeader>

      <FilterBar
        className="mb-0 px-5 py-3 border-b border-slate-100"
        value={status || "all"}
        onValueChange={(v) => setStatus(v === "all" ? "" : v)}
        tabs={[
          { value: "all", label: "All", count: counts?.all },
          ...PARTNER_FORM_STATUS_TABS.map((t) => ({ value: t, label: t, count: counts?.[t] })),
        ]}
        search={keyword}
        onSearchChange={setKeyword}
        searchPlaceholder="Search by name, email, or business"
      >
        <DateRangeFilter value={range} onChange={setRange} />
      </FilterBar>

      {error ? (
        <div className="p-5">
          <Notice tone="error">{error}</Notice>
        </div>
      ) : !loaded ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">Loading…</p>
      ) : data.totalCount === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">
          {filtered ? "No submissions match these filters." : "No submissions yet."}
        </p>
      ) : (
        <div className={cn("transition-opacity", loading && "opacity-60")}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTH {...thProps("submittedAt")}>Submitted</SortableTH>
                  <SortableTH {...thProps("name")}>Name</SortableTH>
                  <SortableTH {...thProps("business")}>Business / Entity</SortableTH>
                  <SortableTH {...thProps("email")}>Email</SortableTH>
                  <Th>Docs</Th>
                  <SortableTH {...thProps("status")}>Status</SortableTH>
                  <Th>Action</Th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((f) => {
                  const isNewPartner = !f.targetUserId;
                  const pending = f.status !== "Processed" && !f.processedUserId;
                  return (
                    <TableRow key={f.id}>
                      <TableCell className="whitespace-nowrap text-slate-600">
                        {formatDateTime(f.submittedAt, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}
                      </TableCell>
                      <TableCell>{f.contactName || <Dash />}</TableCell>
                      <TableCell>
                        {f.legalName || <span className="text-slate-500 italic">{f.ownershipType || <Dash />}</span>}
                      </TableCell>
                      <TableCell>{f.email || <Dash />}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {f.hasLicense && (
                          <button type="button" className="text-brand-600 hover:underline mr-2"
                             onClick={() => viewDocument(f.id, "license")}>License</button>
                        )}
                        {f.hasVoidCheck && (
                          <button type="button" className="text-brand-600 hover:underline"
                             onClick={() => viewDocument(f.id, "check")}>Check</button>
                        )}
                        {!f.hasLicense && !f.hasVoidCheck && <Dash />}
                      </TableCell>
                      <TableCell>
                        <Badge variant={f.status === "Processed" ? "success" : "warning"}>{f.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {created[f.id] ? (
                          <span className="text-xs text-emerald-700">
                            Created · temp password: <code className="font-mono select-all">{created[f.id]}</code>
                          </span>
                        ) : isNewPartner && pending ? (
                          <Button type="button" size="sm" variant="outline" onClick={() => createUser(f)} disabled={busyId === f.id}>
                            {busyId === f.id ? "Creating…" : "Create New User"}
                          </Button>
                        ) : (
                          <Dash />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Pagination page={data} onPageChange={setPage} busy={loading} />
        </div>
      )}
    </Card>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <TableHead className="text-xs font-medium uppercase tracking-wide whitespace-nowrap">{children}</TableHead>;
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
